// AssertionReportService — exports assertion results as JSON or formatted text.

import type { AssertionResult, AssertionSummary, SuiteResult } from "../store/slices/simulationSlice.ts";

export interface AssertionReport {
  generatedAt: string;
  scenarioId: string | null;
  scenarioLabel: string | null;
  simulationStatus: string;
  summary: AssertionSummary;
  results: AssertionResult[];
  actualPath: string[];
  suiteResults?: SuiteResult[];
}

/**
 * Build a structured assertion report from current state.
 */
export function buildReport(params: {
  scenarioId: string | null;
  scenarioLabel: string | null;
  simulationStatus: string;
  summary: AssertionSummary;
  results: AssertionResult[];
  actualPath: string[];
  suiteResults?: SuiteResult[];
}): AssertionReport {
  return {
    generatedAt: new Date().toISOString(),
    ...params,
  };
}

/**
 * Export report as a downloadable JSON file.
 */
export function downloadReportAsJSON(report: AssertionReport): void {
  const filename = report.scenarioId
    ? `facet-report-${report.scenarioId}-${Date.now()}.json`
    : `facet-report-${Date.now()}.json`;

  const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}

/**
 * Format report as human-readable text for clipboard or display.
 */
export function formatReportAsText(report: AssertionReport): string {
  const lines: string[] = [];

  lines.push("FACET ASSERTION REPORT");
  lines.push(`Generated: ${report.generatedAt}`);
  lines.push(`Scenario: ${report.scenarioLabel ?? "N/A"} (${report.scenarioId ?? "N/A"})`);
  lines.push(`Status: ${report.simulationStatus}`);
  lines.push("");
  lines.push(`SUMMARY: ${report.summary.passed}/${report.summary.total} passed, ${report.summary.failed} failed`);

  if (Object.keys(report.summary.byType).length > 0) {
    lines.push("");
    lines.push("BY TYPE:");
    for (const [type, counts] of Object.entries(report.summary.byType)) {
      const status = counts.failed > 0 ? "FAIL" : "PASS";
      lines.push(`  [${status}] ${type}: ${counts.passed}/${counts.passed + counts.failed}`);
    }
  }

  if (report.results.length > 0) {
    lines.push("");
    lines.push("DETAILS:");
    for (const r of report.results) {
      const icon = r.passed ? "PASS" : "FAIL";
      lines.push(`  [${icon}] ${r.assertionId}: ${r.detail}`);
    }
  }

  if (report.suiteResults && report.suiteResults.length > 0) {
    lines.push("");
    lines.push("SUITE RESULTS:");
    for (const sr of report.suiteResults) {
      const icon = sr.verdict === "pass" ? "PASS" : "FAIL";
      lines.push(`  [${icon}] ${sr.scenarioLabel} — ${sr.assertionSummary.passed}/${sr.assertionSummary.total} (${sr.durationMs}ms)`);
    }
  }

  return lines.join("\n");
}
